// @ts-check
import "katex/dist/katex.min.css"
import "./canvas.css"
import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import { Markdown } from "tiptap-markdown"
import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from "react"
import { useReducedMotion } from "framer-motion"
import { DiffHunkExtension } from "./DiffHunkExtension"
import { injectDiffMarkers } from "./diffMarkers"
import { TableMarkdown, TableCell, TableHeader, TableRow } from "./TableMarkdownExtension"
import { MarkdownInlineMath, MarkdownBlockMath } from "./mathMarkdown"
import { Sparkles, Bold, Italic, Underline as UnderlineIcon, Sigma } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"


const DEBOUNCE_MS = 1500
const FLASH_MS = 1400

/**
 * @param {string} content
 * @param {Array<{id: string, status: string}>} pendingDiffs
 */
function makeContentSignature(content, pendingDiffs) {
  return `${content}|${pendingDiffs.map(d => `${d.id}:${d.status}`).join(",")}`
}

/**
 * @param {{
 *   content: string,
 *   pendingDiffs: Array<{id: string, isFullRewrite?: boolean, old_string?: string, new_string: string, rationale?: string, status: string}>,
 *   onSave: (markdown: string) => void,
 *   onAcceptDiff: (diffId: string) => void,
 *   onRejectDiff: (diffId: string) => void,
 *   onSuggestEdits?: (selectedText: string) => void,
 *   readOnly?: boolean,
 *   animateDiffId?: string | null,
 * }} props
 * @param {React.Ref<{ insertInlineMath: (latex?: string) => void, focusDiff: (id: string) => boolean }>} ref
 */
const CanvasEditor = forwardRef(function CanvasEditor({ content, pendingDiffs, onSave, onAcceptDiff, onRejectDiff, onSuggestEdits, readOnly = false, animateDiffId = null }, ref) {
  const debounceTimer = useRef(/** @type {ReturnType<typeof setTimeout> | null} */(null))
  // Tracks the (content, pendingDiffs) combination last applied to the editor,
  // so the sync effect below only calls setContent when something actually
  // changed — initialized from props so it matches the useEditor() call
  // below and doesn't redundantly reset the editor right after mount.
  const appliedSignatureRef = useRef(makeContentSignature(content, pendingDiffs))
  // Markdown the editor gives for the last content set from the server. The
  // markdown round trip is not exact (single line breaks can change), so a
  // save that only repeats it would overwrite the document with a lossy copy.
  const baselineMarkdownRef = useRef(/** @type {string | null} */(null))
  const scrollerRef = useRef(/** @type {HTMLDivElement | null} */(null))
  const lastAnimatedIdRef = useRef(/** @type {string | null} */(null))
  const reduceMotion = useReducedMotion()
  const [selectedText, setSelectedText] = useState("")
  const [, forceFormatStateTick] = useState(0)

  const editor = useEditor({
    extensions: [StarterKit.configure({ underline: false }), Underline, TableMarkdown, TableRow, TableHeader, TableCell, Markdown, DiffHunkExtension, MarkdownInlineMath, MarkdownBlockMath],
    content: injectDiffMarkers(content, pendingDiffs),
    onCreate({ editor }) {
      baselineMarkdownRef.current = editor.storage.markdown.getMarkdown()
    },
    onUpdate({ editor }) {
      clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => {
        const markdown = editor.storage.markdown.getMarkdown()
        if (markdown === baselineMarkdownRef.current) return
        baselineMarkdownRef.current = markdown
        onSave(markdown)
      }, DEBOUNCE_MS)
    },
    onSelectionUpdate({ editor }) {
      const { from, to, empty } = editor.state.selection
      setSelectedText(empty ? "" : editor.state.doc.textBetween(from, to, " "))
    },
    onTransaction() {
      // Re-render so toolbar active-state (bold/italic/heading) stays in sync
      forceFormatStateTick(tick => tick + 1)
    },
  })

  const focusDiff = useCallback((/** @type {string} */ id, { flash = true } = {}) => {
    const card = editor?.view.dom.querySelector(`[data-diff-id="${CSS.escape(id)}"]`)
    if (!card) return false
    card.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" })
    if (flash) {
      card.classList.remove("canvas-diff-flash")
      void /** @type {HTMLElement} */(card).offsetWidth // restart the animation on a repeat click
      card.classList.add("canvas-diff-flash")
      setTimeout(() => card.classList.remove("canvas-diff-flash"), FLASH_MS)
    }
    return true
  }, [editor, reduceMotion])

  // A review would overwrite typing, so the document is read-only while it runs.
  useEffect(() => {
    editor?.setEditable(!readOnly, false)
  }, [editor, readOnly])

  // Wire accept/reject callbacks into the extension storage
  useEffect(() => {
    if (!editor) return
    editor.extensionStorage.diffHunk.onAccept = onAcceptDiff
    editor.extensionStorage.diffHunk.onReject = onRejectDiff
  }, [editor, onAcceptDiff, onRejectDiff])

  // Re-derive editor content whenever the base content or the pending-diffs
  // list changes (accept/reject, undo/redo, or the post-stream refetch).
  // Markers are spliced into the RAW markdown string before parsing (see
  // injectDiffMarkers), so a diff hunk lands correctly even when its
  // old_string overlaps markdown syntax (bold/headings) that the old
  // post-parse text search couldn't see. This does mean each change fully
  // reparses the doc rather than patching it in place — an accepted
  // trade-off now that diffs are shown once the review finishes rather than
  // patched in live while streaming.
  useEffect(() => {
    if (!editor) return
    const signature = makeContentSignature(content, pendingDiffs)
    if (appliedSignatureRef.current === signature) return
    appliedSignatureRef.current = signature
    const isNewCard = animateDiffId != null && animateDiffId !== lastAnimatedIdRef.current
    editor.extensionStorage.diffHunk.animateId = isNewCard ? animateDiffId : null
    lastAnimatedIdRef.current = animateDiffId
    const scrollTop = scrollerRef.current?.scrollTop ?? 0
    // tiptap v3 emits an update for setContent by default, which made every
    // accept, reject or refetch autosave the editor's copy as a user edit.
    clearTimeout(debounceTimer.current)
    editor.commands.setContent(injectDiffMarkers(content, pendingDiffs), { emitUpdate: false })
    baselineMarkdownRef.current = editor.storage.markdown.getMarkdown()
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollTop
    if (isNewCard) requestAnimationFrame(() => focusDiff(animateDiffId, { flash: false }))
  }, [content, pendingDiffs, editor, animateDiffId, focusDiff])

  useEffect(() => () => { clearTimeout(debounceTimer.current) }, [])

  useImperativeHandle(ref, () => ({
    focusDiff,
    insertInlineMath(latex = "x^2") {
      editor?.chain().focus().insertInlineMath({ latex }).run()
    },
    insertBlockMath(latex = "x^2") {
      editor?.chain().focus().insertBlockMath({ latex }).run()
    },
    toggleHeading(level) {
      editor?.chain().focus().toggleHeading({ level }).run()
    },
    toggleBulletList() {
      editor?.chain().focus().toggleBulletList().run()
    },
    toggleOrderedList() {
      editor?.chain().focus().toggleOrderedList().run()
    },
  }), [editor, focusDiff])

  const currentBlockValue = !editor ? "paragraph"
    : editor.isActive("heading", { level: 1 }) ? "h1"
    : editor.isActive("heading", { level: 2 }) ? "h2"
    : editor.isActive("heading", { level: 3 }) ? "h3"
    : "paragraph"

  function handleBlockValueChange(value) {
    if (!editor) return
    if (value === "paragraph") {
      editor.chain().focus().setParagraph().run()
      return
    }
    const level = { h1: 1, h2: 2, h3: 3 }[value]
    editor.chain().focus().toggleHeading({ level }).run()
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {editor && (
        <div
          aria-disabled={readOnly}
          className={cn(
            "flex items-center gap-1 px-3 sm:px-4 py-1.5 border-b border-border shrink-0 transition-opacity",
            readOnly && "pointer-events-none opacity-50",
          )}
        >
          <Select value={currentBlockValue} onValueChange={handleBlockValueChange}>
            <SelectTrigger className="h-7 w-[9.5rem] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="paragraph">Normal text</SelectItem>
              <SelectItem value="h1">Heading 1</SelectItem>
              <SelectItem value="h2">Heading 2</SelectItem>
              <SelectItem value="h3">Heading 3</SelectItem>
            </SelectContent>
          </Select>

          <FormatButton tooltip="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
            <Bold size={14} />
          </FormatButton>
          <FormatButton tooltip="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
            <Italic size={14} />
          </FormatButton>
          <FormatButton tooltip="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}>
            <UnderlineIcon size={14} />
          </FormatButton>

          <div className="w-px h-5 bg-border mx-1" />

          <FormatButton tooltip="Insert inline equation" onClick={() => editor.chain().focus().insertInlineMath({ latex: "x^2" }).run()}>
            <Sigma size={14} />
          </FormatButton>
        </div>
      )}

      <div ref={scrollerRef} className="canvas-editor relative flex-1 overflow-y-auto px-4 sm:px-8 py-4 sm:py-6">
        <EditorContent
          editor={editor}
          className="prose prose-sm max-w-none dark:prose-invert focus:outline-none"
        />
        {selectedText && onSuggestEdits && !readOnly && (
          <button
            type="button"
            title="Suggest edits for selection"
            onClick={() => onSuggestEdits(selectedText)}
            className="fixed right-4 sm:right-6 bottom-6 z-10 flex items-center justify-center h-10 w-10 rounded-full bg-primary text-primary-foreground shadow-lg hover:opacity-90 transition-opacity"
          >
            <Sparkles size={16} />
          </button>
        )}
      </div>
    </div>
  )
})

function FormatButton({ tooltip, active, onClick, children }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      title={tooltip}
      onClick={onClick}
      className={cn("h-7 w-7 rounded-md", active && "bg-muted text-foreground")}
    >
      {children}
    </Button>
  )
}

export default CanvasEditor
